import { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence, useInView, useReducedMotion } from 'motion/react';
import { Check, CornerDownLeft, AlertTriangle } from 'lucide-react';
import { DEMO_SCENARIOS } from '../../../content/servicesCopy';

/*
 * Show-don't-tell demos for the live modules.
 *
 * Each one is a miniature of the real product screen. They cycle through
 * several scenarios rather than replaying a single loop — one repeating clip
 * reads as a canned graphic, several read as a system handling whatever
 * arrives. Scenarios come from DEMO_SCENARIOS so adding one never touches
 * this file.
 *
 * `useScenarioStepper` owns both clocks: the step index inside a scenario,
 * and advancing to the next scenario once a scenario finishes. Under
 * prefers-reduced-motion it pins to the final frame of the first scenario and
 * never animates.
 */
function useScenarioStepper(scenarios, stepsFor, { stepMs = 1400, holdMs = 4200 } = {}) {
  const ref = useRef(null);
  const inView = useInView(ref, { amount: 0.35 });
  const reduce = useReducedMotion();

  const [scenarioIdx, setScenarioIdx] = useState(0);
  const total = stepsFor(scenarios[scenarioIdx]);
  const [step, setStep] = useState(reduce ? total : 0);

  useEffect(() => {
    if (reduce) {
      setStep(stepsFor(scenarios[0]));
      return;
    }
    if (!inView) return;

    let timer;
    const tick = (next) => {
      setStep(next);
      if (next < total) {
        timer = setTimeout(() => tick(next + 1), stepMs);
        return;
      }
      // Scenario finished — hold on the result, then move to the next one.
      timer = setTimeout(() => {
        setStep(0);
        setScenarioIdx((i) => (i + 1) % scenarios.length);
      }, holdMs);
    };
    timer = setTimeout(() => tick(1), 700);
    return () => clearTimeout(timer);
  }, [inView, reduce, scenarioIdx, total, scenarios, stepsFor, stepMs, holdMs]);

  return { ref, step, scenarioIdx, scenario: scenarios[scenarioIdx], reduce };
}

/* Scenario tabs — also tell the reader these are different situations. */
function ScenarioBar({ scenarios, activeIdx }) {
  return (
    <div className="flex items-center gap-1.5 mb-2.5 flex-wrap" aria-hidden="true">
      {scenarios.map((s, i) => (
        <span
          key={s.label}
          className="fa-ref transition-colors"
          style={{
            fontSize: 8.5,
            padding: '3px 7px',
            borderRadius: 3,
            border: `1px solid ${i === activeIdx ? 'var(--fa-signal)' : 'var(--fa-rule)'}`,
            color: i === activeIdx ? 'var(--fa-signal)' : 'var(--fa-muted)',
            background: i === activeIdx ? 'var(--fa-signal-wash)' : 'transparent',
          }}
        >
          {s.label}
        </span>
      ))}
    </div>
  );
}

function Shell({ chrome, children, minHeight = 190 }) {
  return (
    <div
      className="fa-grid fa-grid--fine overflow-hidden"
      style={{ background: 'var(--fa-paper)', border: '1px solid var(--fa-rule)', borderRadius: 5 }}
    >
      <div className="flex items-center gap-2 px-3 py-2" style={{ borderBottom: '1px solid var(--fa-rule)' }}>
        <span className="fa-status-dot fa-status-dot--live" style={{ width: 6, height: 6 }} aria-hidden="true" />
        <span className="fa-ref" style={{ fontSize: 9 }}>{chrome}</span>
      </div>
      <div className="px-3.5 py-3.5" style={{ minHeight }}>{children}</div>
    </div>
  );
}

function Reveal({ show, children, className = '' }) {
  return (
    <AnimatePresence initial={false}>
      {show && (
        <motion.div
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.45, ease: [0.2, 0.7, 0.2, 1] }}
          className={className}
        >
          {children}
        </motion.div>
      )}
    </AnimatePresence>
  );
}

function Bubble({ side, children }) {
  const agent = side === 'agent';
  return (
    <div className={`flex ${agent ? 'justify-start' : 'justify-end'}`}>
      <div
        className="text-[12px] px-3 py-2 max-w-[88%] leading-snug"
        style={{
          background: agent ? 'var(--fa-signal-wash)' : 'var(--fa-surface)',
          color: agent ? 'var(--fa-signal)' : 'var(--fa-ink)',
          border: `1px solid ${agent ? 'var(--fa-signal)' : 'var(--fa-rule)'}`,
          borderRadius: 6,
        }}
      >
        {children}
      </div>
    </div>
  );
}

function Typing() {
  return (
    <div className="flex items-center gap-1.5 pl-1" style={{ color: 'var(--fa-muted)' }}>
      {[0, 1, 2].map((i) => (
        <motion.span
          key={i}
          style={{ width: 4, height: 4, borderRadius: '50%', background: 'currentColor', display: 'block' }}
          animate={{ opacity: [0.25, 1, 0.25] }}
          transition={{ duration: 1, repeat: Infinity, delay: i * 0.15 }}
        />
      ))}
    </div>
  );
}

/* ── SYS-01 · support ─────────────────────────────────────────────────── */
// One step per turn, plus a typing beat before each agent reply, plus the close.
const chatSteps = (s) => s.turns.length + s.turns.filter((t) => t.side === 'agent').length + 1;

function ChatDemo({ large }) {
  const scenarios = DEMO_SCENARIOS.chat;
  const { ref, step, scenarioIdx, scenario } = useScenarioStepper(scenarios, chatSteps, { stepMs: 1500 });

  // Walk the turns, allotting an extra step to the typing indicator that
  // precedes every agent reply.
  let cursor = 0;
  const rendered = scenario.turns.map((t, i) => {
    const typingAt = t.side === 'agent' ? ++cursor : null;
    const turnAt = ++cursor;
    return { ...t, i, typingAt, turnAt };
  });

  return (
    <div ref={ref}>
      <ScenarioBar scenarios={scenarios} activeIdx={scenarioIdx} />
      <Shell chrome={scenario.chrome} minHeight={large ? 300 : 190}>
        <div className="flex flex-col gap-2">
          {rendered.map((t) => (
            <div key={t.i} className="flex flex-col gap-2">
              {t.typingAt !== null && (
                <Reveal show={step >= t.typingAt && step < t.turnAt}><Typing /></Reveal>
              )}
              <Reveal show={step >= t.turnAt}>
                <Bubble side={t.side}>{t.text}</Bubble>
              </Reveal>
            </div>
          ))}
          <Reveal show={step >= cursor + 1}>
            <div className="flex items-center gap-1.5 mt-1">
              <Check size={11} style={{ color: 'var(--fa-signal)' }} />
              <span className="fa-ref" style={{ fontSize: 9 }}>{scenario.close}</span>
            </div>
          </Reveal>
        </div>
      </Shell>
    </div>
  );
}

/* ── SYS-02 · ops ─────────────────────────────────────────────────────── */
const workflowSteps = (s) => s.steps.length + 2; // trigger + each step + close

function WorkflowDemo({ large }) {
  const scenarios = DEMO_SCENARIOS.workflow;
  const { ref, step, scenarioIdx, scenario } = useScenarioStepper(scenarios, workflowSteps, { stepMs: 1050 });

  return (
    <div ref={ref}>
      <ScenarioBar scenarios={scenarios} activeIdx={scenarioIdx} />
      <Shell chrome={scenario.chrome} minHeight={large ? 300 : 190}>
        <div className="flex flex-col gap-2">
          <Reveal show={step >= 1}>
            <div
              className="flex items-center gap-2 px-2.5 py-2"
              style={{ background: 'var(--fa-signal-wash)', border: '1px solid var(--fa-signal)', borderRadius: 4 }}
            >
              <CornerDownLeft size={11} style={{ color: 'var(--fa-signal)' }} />
              <span className="text-[11.5px] font-medium" style={{ color: 'var(--fa-signal)' }}>
                {scenario.trigger}
              </span>
            </div>
          </Reveal>
          {scenario.steps.map((label, i) => {
            // A step that reports an exception gets an amber marker instead of
            // a tick — the honest version of "it ran" includes "it caught one".
            const isFlag = /^flagged/i.test(label);
            return (
              <Reveal key={label} show={step >= i + 2}>
                <div className="flex items-center gap-2.5 pl-3">
                  <span
                    className="flex items-center justify-center flex-shrink-0"
                    style={{
                      width: 14, height: 14, borderRadius: 3,
                      background: isFlag ? 'transparent' : 'var(--fa-signal)',
                      border: isFlag ? '1px solid var(--fa-muted)' : 'none',
                      color: isFlag ? 'var(--fa-muted)' : 'var(--fa-paper)',
                    }}
                  >
                    {isFlag ? <AlertTriangle size={9} /> : <Check size={9} strokeWidth={3} />}
                  </span>
                  <span className="text-[11.5px]" style={{ color: 'var(--fa-ink)' }}>{label}</span>
                </div>
              </Reveal>
            );
          })}
          <Reveal show={step >= scenario.steps.length + 2}>
            <div className="fa-ref pl-3" style={{ fontSize: 9 }}>{scenario.close}</div>
          </Reveal>
        </div>
      </Shell>
    </div>
  );
}

/* ── SYS-03 · sales ───────────────────────────────────────────────────── */
const pipelineSteps = (s) => s.rows.length + 2; // each row + draft + reply

function PipelineDemo({ large }) {
  const scenarios = DEMO_SCENARIOS.pipeline;
  const { ref, step, scenarioIdx, scenario } = useScenarioStepper(scenarios, pipelineSteps, { stepMs: 1200 });

  return (
    <div ref={ref}>
      <ScenarioBar scenarios={scenarios} activeIdx={scenarioIdx} />
      <Shell chrome={scenario.chrome} minHeight={large ? 300 : 190}>
        <div className="flex flex-col gap-2">
          {scenario.rows.map((p, i) => (
            <Reveal key={p.co} show={step >= i + 1}>
              <div
                className="flex items-baseline gap-2 px-2.5 py-1.5"
                style={{ border: '1px solid var(--fa-rule)', borderRadius: 4, background: 'var(--fa-surface)' }}
              >
                <span className="text-[11.5px] font-medium truncate" style={{ color: 'var(--fa-ink)' }}>{p.co}</span>
                <span className="fa-ref truncate" style={{ fontSize: 8.5 }}>{p.who}</span>
                <span
                  className="fa-ref ml-auto truncate"
                  style={{ fontSize: 8.5, color: 'var(--fa-signal)', textTransform: 'none', letterSpacing: '0.02em' }}
                >
                  {p.sig}
                </span>
              </div>
            </Reveal>
          ))}
          <Reveal show={step >= scenario.rows.length + 1}>
            <div
              className="text-[11px] leading-snug px-2.5 py-2 mt-0.5"
              style={{
                background: 'var(--fa-signal-wash)',
                border: '1px solid var(--fa-signal)',
                borderRadius: 4,
                color: 'var(--fa-signal)',
              }}
            >
              {scenario.draft}
            </div>
          </Reveal>
          <Reveal show={step >= scenario.rows.length + 2}>
            <div className="flex items-center gap-1.5">
              <CornerDownLeft size={11} style={{ color: 'var(--fa-signal)' }} />
              <span className="fa-ref" style={{ fontSize: 9 }}>{scenario.close}</span>
            </div>
          </Reveal>
        </div>
      </Shell>
    </div>
  );
}

/*
 * Spec panel — what an in-build module shows instead of a running demo.
 * Deliberately looks like a datasheet rather than a product screen, so it
 * never reads as a thing you can buy today.
 */
function SpecPanel({ spec, large }) {
  return (
    <div>
      <div className="flex items-center gap-1.5 mb-2.5" aria-hidden="true">
        <span
          className="fa-ref"
          style={{
            fontSize: 8.5, padding: '3px 7px', borderRadius: 3,
            border: '1px dashed var(--fa-muted)', color: 'var(--fa-muted)',
          }}
        >
          {spec.stage}
        </span>
      </div>
      <div
        className="fa-grid fa-grid--fine overflow-hidden"
        style={{ background: 'var(--fa-paper)', border: '1px dashed var(--fa-rule)', borderRadius: 5 }}
      >
        <div className="flex items-center gap-2 px-3 py-2" style={{ borderBottom: '1px solid var(--fa-rule)' }}>
          <span className="fa-status-dot fa-status-dot--dev" style={{ width: 6, height: 6 }} aria-hidden="true" />
          <span className="fa-ref" style={{ fontSize: 9 }}>Specification</span>
        </div>
        <div className="px-3.5 py-3" style={{ minHeight: large ? 300 : 190 }}>
          <dl className="flex flex-col">
            {spec.rows.map((r, i) => (
              <div
                key={r.k}
                className="flex items-baseline gap-3 py-[7px]"
                style={{ borderBottom: i < spec.rows.length - 1 ? '1px solid var(--fa-rule)' : 'none' }}
              >
                <dt className="fa-ref flex-shrink-0" style={{ fontSize: 8.5, minWidth: 78 }}>{r.k}</dt>
                <dd className="text-[11.5px]" style={{ color: 'var(--fa-ink)' }}>{r.v}</dd>
              </div>
            ))}
          </dl>
          <p className="mt-3 text-[11px] leading-snug" style={{ color: 'var(--fa-muted)' }}>
            {spec.note}
          </p>
        </div>
      </div>
    </div>
  );
}

export default function ServiceDemo({ card, large = false }) {
  // A spec block is the signal, not the status - a module we build to order
  // shows a datasheet rather than a demo of something we do not run.
  if (card.spec) return <SpecPanel spec={card.spec} large={large} />;
  if (card.demo === 'chat')     return <ChatDemo large={large} />;
  if (card.demo === 'workflow') return <WorkflowDemo large={large} />;
  if (card.demo === 'pipeline') return <PipelineDemo large={large} />;
  return null;
}
